<script setup lang="ts">
import ChevronDownIcon from '@/assets/svg/chevron-down-16.svg?component';
import CheckHighlightIcon from '@/assets/svg/conversations/check-highlight-16.svg?component';
import {
  ALERT_MENU_KEY,
  ALERT_NAVIGATION_KEYS,
  ALERT_OPTIONS,
  type AlertNavigationKey,
} from '@/components/Conversations/Message/conversation-alerts-control.constants';
import ConversationIndexService from '@/core/conversations/conversation-index/conversation-index.service';
import { ErrorService } from '@/core/shared/errors/error.service';
import { SnackbarService } from '@/core/shared/snackbar/snackbar.service';
import { type ComponentPublicInstance, computed, nextTick, ref, watch } from 'vue';

const conversationIndexService = new ConversationIndexService();

const isMenuVisible = ref(false);
const isUpdating = ref(false);
const alertOptionRefs = ref<HTMLButtonElement[]>([]);

function isAlertNavigationKey(key: string): key is AlertNavigationKey {
  return ALERT_NAVIGATION_KEYS.includes(key as AlertNavigationKey);
}

const selectedConversationIndex = computed(
  () => conversationIndexService.selectedConversationIndex,
);
const isWatching = computed(() => selectedConversationIndex.value?.isWatching ?? true);
const alertLabel = computed(() => (isWatching.value ? 'Alerts on' : 'Alerts off'));

function getSelectedOptionIndex() {
  return ALERT_OPTIONS.findIndex(({ value }) => value === isWatching.value);
}

function focusAlertOption(index: number) {
  alertOptionRefs.value[index]?.focus();
}

function setAlertOptionRef(element: Element | ComponentPublicInstance | null, index: number) {
  if (element instanceof HTMLButtonElement) {
    alertOptionRefs.value[index] = element;
  }
}

function handleAlertMenuKeydown(event: KeyboardEvent) {
  const optionsLength = alertOptionRefs.value.length;

  if (!isAlertNavigationKey(event.key) || optionsLength === 0) {
    return;
  }

  event.preventDefault();

  const currentIndex = alertOptionRefs.value.findIndex(
    (option) => option === document.activeElement,
  );
  const selectedIndex = getSelectedOptionIndex();
  const baseIndex = currentIndex === -1 ? selectedIndex : currentIndex;

  if (event.key === ALERT_MENU_KEY.FIRST) {
    focusAlertOption(0);
    return;
  }

  if (event.key === ALERT_MENU_KEY.LAST) {
    focusAlertOption(alertOptionRefs.value.length - 1);
    return;
  }

  const direction = event.key === ALERT_MENU_KEY.NEXT ? 1 : -1;
  const nextIndex = (baseIndex + direction + optionsLength) % optionsLength;

  focusAlertOption(nextIndex);
}

async function handleSelectWatchStatus(nextValue: boolean) {
  const conversationId = selectedConversationIndex.value?.conversationId;

  if (!conversationId || nextValue === isWatching.value) {
    return;
  }

  try {
    isUpdating.value = true;
    isMenuVisible.value = false;
    await conversationIndexService.updateConversationWatchStatus(conversationId, nextValue);
    SnackbarService.success(
      nextValue ? 'Conversation alerts turned on' : 'Conversation alerts turned off',
    );
  } catch (error) {
    ErrorService.captureException(error);
    SnackbarService.criticalAlt('Failed to update conversation alerts');
  } finally {
    isUpdating.value = false;
  }
}

watch(
  () => selectedConversationIndex.value?.conversationId,
  () => {
    isMenuVisible.value = false;
    isUpdating.value = false;
    alertOptionRefs.value = [];
  },
);

watch(isMenuVisible, async (value) => {
  if (!value) {
    alertOptionRefs.value = [];
    return;
  }

  await nextTick();
  focusAlertOption(getSelectedOptionIndex());
});
</script>

<template>
  <div v-if="selectedConversationIndex" class="text-xs">
    <v-menu
      :model-value="isMenuVisible"
      :close-on-content-click="false"
      location="top end"
      :offset="8"
      transition="slide-y-reverse-transition"
      @update:model-value="isMenuVisible = $event"
    >
      <template #activator="{ props }">
        <button
          v-bind="props"
          type="button"
          class="flex min-w-[102px] items-center justify-between gap-2 rounded-full border border-tint-100 bg-white px-3 py-1.5 text-xs font-semibold text-shade-900 transition-colors hover:border-tint-200 hover:bg-tint-0 disabled:cursor-not-allowed disabled:opacity-60"
          aria-label="Toggle conversation alerts"
          data-test="conversation-alerts-trigger"
          :aria-busy="isUpdating"
          :disabled="isUpdating"
        >
          <span class="text-left">{{ alertLabel }}</span>
          <ChevronDownIcon
            class="h-4 w-4 shrink-0 text-shade-600 transition-transform"
            :class="{ 'rotate-180': isMenuVisible }"
          />
        </button>
      </template>

      <div
        class="menu-card w-[288px] bg-white/95 backdrop-blur-[4px]"
        role="menu"
        aria-label="Conversation alerts"
        @keydown="handleAlertMenuKeydown"
      >
        <button
          v-for="(option, index) in ALERT_OPTIONS"
          :key="option.label"
          :ref="(element) => setAlertOptionRef(element, index)"
          type="button"
          role="menuitemradio"
          :aria-checked="option.value === isWatching"
          :data-test="`conversation-alerts-option-${option.value ? 'on' : 'off'}`"
          class="menu-item items-start gap-3 py-2.5 transition-colors"
          :class="{ 'menu-row-selected': option.value === isWatching }"
          @click="handleSelectWatchStatus(option.value)"
        >
          <component :is="option.icon" class="mt-0.5 h-4 w-4 shrink-0" />

          <div class="flex-1">
            <div class="font-semibold">{{ option.label }}</div>
            <div class="mt-1 text-xs font-normal text-shade-600">{{ option.description }}</div>
          </div>

          <CheckHighlightIcon v-if="option.value === isWatching" class="mt-0.5 h-4 w-4 shrink-0" />
        </button>
      </div>
    </v-menu>
  </div>
</template>
